import {
  Fragment,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { ScoreMiniChartPoint } from "../../../shared/components/ScoreMiniChart.tsx";
import { SimpleMarkdown } from "../../../shared/components/SimpleMarkdown.tsx";
import {
  formatWorkflowTimelineEventLabel,
  isVisibleWorkflowTimelineEvent,
} from "../../project-detail/lib/workflow-timeline.ts";
import { useEvWorkstationProjectDetailQuery } from "../hooks/use-ev-workstation-queries.ts";
import {
  type EvWorkflowSortDir,
  type EvWorkflowSortKey,
  orderEvWorkflowItems,
} from "../lib/queue-order.ts";
import type {
  EvIntroContact,
  EvIntroContactUpdateInput,
  EvIntroStatus,
  EvWorkflowEvent,
  EvWorkflowPatch,
  EvWorkstationProject,
} from "../types/index.ts";

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function showNativeDatePicker(input: HTMLInputElement): void {
  if (typeof input.showPicker !== "function") return;
  try {
    input.showPicker();
  } catch {
    // Ignore browsers that gate showPicker behind stricter user-activation rules.
  }
}

function formatScore(value: number | string | null): string {
  if (value == null) return "—";
  return Number(value).toFixed(1);
}

function getDeltaValue(project: EvWorkstationProject["project"]): number | null {
  if (project.delta_alpha_score != null && Number.isFinite(Number(project.delta_alpha_score))) {
    return Number(project.delta_alpha_score);
  }

  if (project.current_alpha_score == null || project.prev_alpha_score == null) {
    return null;
  }

  return Number(project.current_alpha_score) - Number(project.prev_alpha_score);
}

function formatDelta(value: number | null): string {
  if (value == null) return "—";
  const rounded = Math.round(value * 10) / 10;
  if (Math.abs(rounded) < 0.05) return "0.0";
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}`;
}

function getDeltaTone(value: number | null): "positive" | "negative" | "flat" {
  if (value == null || Math.abs(value) < 0.05) return "flat";
  return value > 0 ? "positive" : "negative";
}

function getScoreHistory(project: EvWorkstationProject["project"]): ScoreMiniChartPoint[] {
  return project.score_history ?? [];
}

function toNum(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

interface CategoryDelta {
  label: string;
  prev: number;
  now: number;
  delta: number;
}

function getCategoryDeltas(project: EvWorkstationProject["project"]): {
  deltas: CategoryDelta[];
  hasPrevData: boolean;
} {
  const categories: {
    label: string;
    now: number | string | null;
    prev: number | string | null | undefined;
  }[] = [
    { label: "Social", now: project.social_points, prev: project.prev_social_points },
    { label: "Backers", now: project.backers_points, prev: project.prev_backers_points },
    { label: "Pedigree", now: project.pedigree_points, prev: project.prev_pedigree_points },
    { label: "Momentum", now: project.momentum_points, prev: project.prev_momentum_points },
    { label: "Dev", now: project.dev_points, prev: project.prev_dev_points },
    { label: "Revenue", now: project.revenue_points, prev: project.prev_revenue_points },
  ];
  const deltas: CategoryDelta[] = [];
  let hasPrevData = false;
  for (const cat of categories) {
    if (cat.prev == null) continue;
    hasPrevData = true;
    const now = toNum(cat.now);
    const prev = toNum(cat.prev);
    const delta = +(now - prev).toFixed(1);
    if (delta !== 0) deltas.push({ label: cat.label, prev, now, delta });
  }
  return { deltas, hasPrevData };
}

function formatSparklineDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getUTCDate()}`;
}

function InlineSparkline({ data }: { data: ScoreMiniChartPoint[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const points = useMemo(
    () =>
      data.filter(
        (d): d is ScoreMiniChartPoint & { alpha_score: number | string } =>
          d.alpha_score != null && Number.isFinite(Number(d.alpha_score))
      ),
    [data]
  );

  const chart = useMemo(() => {
    if (points.length < 2) return null;
    const W = 68;
    const H = 20;
    const P = 2;
    const scores = points.map((p) => Number(p.alpha_score));
    const min = Math.min(...scores) - 2;
    const max = Math.max(...scores) + 2;
    const range = max - min || 1;
    const coords = scores.map((s, i) => ({
      x: P + (i / (scores.length - 1)) * (W - P * 2),
      y: H - P - ((s - min) / range) * (H - P * 2),
    }));
    const line = coords.map((c) => `${c.x},${c.y}`).join(" ");
    const trending = (scores[scores.length - 1] ?? 0) >= (scores[0] ?? 0);
    return { W, H, coords, line, strokeColor: trending ? "#6ee7b7" : "#fca5a5" };
  }, [points]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg || !chart) return;
      const rect = svg.getBoundingClientRect();
      const relX = e.clientX - rect.left;
      const scaleX = chart.W / rect.width;
      const svgX = relX * scaleX;
      let closest = 0;
      let closestDist = Math.abs((chart.coords[0]?.x ?? 0) - svgX);
      for (let i = 1; i < chart.coords.length; i++) {
        const dist = Math.abs((chart.coords[i]?.x ?? 0) - svgX);
        if (dist < closestDist) {
          closest = i;
          closestDist = dist;
        }
      }
      setHoverIdx(closest);
      setTooltipPos({ top: rect.top - 30, left: e.clientX });
    },
    [chart]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverIdx(null);
    setTooltipPos(null);
  }, []);

  if (!chart) return null;

  const hoveredPoint = hoverIdx !== null ? points[hoverIdx] : null;
  const hoveredCoord = hoverIdx !== null ? chart.coords[hoverIdx] : null;

  return (
    <span className="sparkline-wrap">
      <svg
        ref={svgRef}
        width={chart.W}
        height={chart.H}
        viewBox={`0 0 ${chart.W} ${chart.H}`}
        style={{ display: "block", flexShrink: 0, cursor: "crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <title>Score trend</title>
        <polyline
          fill="none"
          stroke={chart.strokeColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={chart.line}
        />
        {hoveredCoord && (
          <circle cx={hoveredCoord.x} cy={hoveredCoord.y} r={3} fill={chart.strokeColor} />
        )}
      </svg>
      {hoveredPoint &&
        tooltipPos &&
        createPortal(
          <div className="sparkline-tooltip" style={{ top: tooltipPos.top, left: tooltipPos.left }}>
            {Number(hoveredPoint.alpha_score).toFixed(1)} &middot;{" "}
            {formatSparklineDate(hoveredPoint.snapshot_date)}
          </div>,
          document.body
        )}
    </span>
  );
}

function RankChangePopoverContent({ project }: { project: EvWorkstationProject["project"] }) {
  const { deltas, hasPrevData } = getCategoryDeltas(project);
  const hasPrevAlpha = project.prev_alpha_score != null;
  if (!hasPrevData && !hasPrevAlpha) return null;

  const prevTotal = toNum(project.prev_alpha_score ?? null);
  const nowTotal = toNum(project.current_alpha_score);
  const totalDelta = +(nowTotal - prevTotal).toFixed(1);

  if (deltas.length === 0 && totalDelta === 0) {
    return (
      <div className="rank-popover-hint">Scores unchanged — rank shifted by other projects</div>
    );
  }
  return (
    <>
      <div className="rank-popover-header">
        <span>Category</span>
        <span>7d ago</span>
        <span>Now</span>
        <span>Delta</span>
      </div>
      <div className="rank-popover-row rank-popover-total">
        <span className="rank-popover-label">Total</span>
        <span className="rank-popover-val">{prevTotal.toFixed(1)}</span>
        <span className="rank-popover-val">{nowTotal.toFixed(1)}</span>
        <span
          className={`rank-popover-delta ${totalDelta > 0 ? "rank-popover-up" : totalDelta < 0 ? "rank-popover-down" : ""}`}
        >
          {totalDelta > 0 ? `+${totalDelta.toFixed(1)}` : totalDelta.toFixed(1)}
        </span>
      </div>
      {deltas.map((d) => (
        <div key={d.label} className="rank-popover-row">
          <span className="rank-popover-label">{d.label}</span>
          <span className="rank-popover-val">{d.prev.toFixed(1)}</span>
          <span className="rank-popover-val">{d.now.toFixed(1)}</span>
          <span
            className={`rank-popover-delta ${d.delta > 0 ? "rank-popover-up" : "rank-popover-down"}`}
          >
            {d.delta > 0 ? `+${d.delta.toFixed(1)}` : d.delta.toFixed(1)}
          </span>
        </div>
      ))}
    </>
  );
}

function DeltaBadge({
  project,
  delta,
  tone,
}: {
  project: EvWorkstationProject["project"];
  delta: number | null;
  tone: "positive" | "negative" | "flat";
}) {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  const handleEnter = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left });
    }
    setHovered(true);
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover-only popover, no interactive action
    <span
      ref={ref}
      className="delta-badge-wrapper"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        className={`mono ev-workstation-table__delta-value ev-workstation-table__delta-value--${tone}`}
      >
        {formatDelta(delta)}
      </span>
      {hovered &&
        pos &&
        createPortal(
          <div className="rank-popover--fixed" style={{ top: pos.top, left: pos.left }}>
            <RankChangePopoverContent project={project} />
          </div>,
          document.body
        )}
    </span>
  );
}
function formatIntroMatch(contact: EvIntroContact): string {
  const contactRoute = contact.contact.isTelegramGroup
    ? "Telegram group"
    : (contact.contact.role ?? "Contact");
  return `${contact.contact.name} · ${contact.fundName} · ${contactRoute}`;
}

function getIntroContactDisplayLabel(contact: EvIntroContact): string {
  return contact.contact.isTelegramGroup ? `TG ${contact.contact.name}` : contact.contact.name;
}

function getIntroStatusOptionLabel(status: EvIntroStatus): string {
  switch (status) {
    case "not_asked":
      return "Not asked";
    case "asked":
      return "Asked";
    case "intro_made":
      return "Intro made";
    case "no_response":
      return "No response";
    case "not_interested":
      return "Not interested";
    case "follow_up_needed":
      return "Follow up needed";
  }
}

function getIntroStatusClassName(status: EvIntroStatus): string {
  return `ev-workstation-table__select--intro-status-${status}`;
}

function normalizeActionPlanValue(value: string): string | null {
  const normalized = value.trim();
  return normalized ? normalized : null;
}

function getActionPlanPreviewText(value: string | null): string {
  return normalizeActionPlanValue(value ?? "") ?? "No next action set.";
}

function getProjectFollowUpDate(workflow: EvWorkstationProject["workflow"]): string | null {
  return workflow.projectFollowUpDate ?? workflow.followUpDate ?? null;
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function getActionPlanButtonLabel(): string {
  return "View Action Plan";
}

function getRecentActionEvents(events: EvWorkflowEvent[]): EvWorkflowEvent[] {
  return events
    .filter(isVisibleWorkflowTimelineEvent)
    .slice()
    .sort((left, right) => {
      const leftTime = new Date(left.createdAt).getTime();
      const rightTime = new Date(right.createdAt).getTime();
      return rightTime - leftTime;
    })
    .slice(0, 5);
}

interface ActionPlanSaveInput {
  nextAction: string | null;
  note: string | null;
}

interface ExpandedActionPlanRowProps {
  item: EvWorkstationProject;
  disabled: boolean;
  colSpan: number;
  onClose: () => void;
  onSave: (projectId: string, input: ActionPlanSaveInput) => Promise<void>;
}

function ExpandedActionPlanRow({
  item,
  disabled,
  colSpan,
  onClose,
  onSave,
}: ExpandedActionPlanRowProps) {
  const detailQuery = useEvWorkstationProjectDetailQuery(item.project.id, true);
  const detail = detailQuery.data;
  const [nextActionDraft, setNextActionDraft] = useState(item.workflow.nextAction ?? "");
  const [noteDraft, setNoteDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const currentNextAction = detail?.workflow.nextAction ?? item.workflow.nextAction ?? null;
  const recentEvents = useMemo(() => getRecentActionEvents(detail?.events ?? []), [detail?.events]);

  useEffect(() => {
    setNextActionDraft(detail?.workflow.nextAction ?? item.workflow.nextAction ?? "");
  }, [detail?.workflow.nextAction, item.workflow.nextAction]);

  async function handleSave() {
    const nextAction = normalizeActionPlanValue(nextActionDraft);
    const note = normalizeActionPlanValue(noteDraft);
    const nextActionChanged = nextAction !== currentNextAction;

    if (!nextActionChanged && !note) {
      onClose();
      return;
    }

    try {
      setIsSaving(true);
      await onSave(item.project.id, { nextAction, note });
      setNoteDraft("");
      onClose();
    } finally {
      setIsSaving(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      if (!disabled && !isSaving) {
        void handleSave();
      }
    }
  }

  const saveDisabled =
    disabled ||
    isSaving ||
    detailQuery.isLoading ||
    (normalizeActionPlanValue(nextActionDraft) === currentNextAction &&
      !normalizeActionPlanValue(noteDraft));

  return (
    <tr className="ev-workstation-table__expanded-row" aria-busy={disabled || isSaving}>
      <td colSpan={colSpan}>
        <div className="ev-workstation-table__expanded-shell">
          <div className="ev-workstation-table__expanded-head">
            <div className="ev-workstation-table__expanded-identity">
              <span className="section-title">
                {item.project.name ?? item.project.twitter_handle}
              </span>
              <span className="mono">@{item.project.twitter_handle}</span>
            </div>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={onClose}
              disabled={disabled || isSaving}
            >
              Back To Queue
            </button>
          </div>

          <div className="ev-workstation-table__expanded-layout">
            <div className="ev-workstation-table__expanded-main">
              <label className="ev-workstation-table__action-editor-field">
                <span>Next Action</span>
                <textarea
                  className="ev-workstation-table__input ev-workstation-table__textarea"
                  rows={5}
                  value={nextActionDraft}
                  placeholder="What needs to happen next?"
                  disabled={disabled || isSaving}
                  onChange={(event) => setNextActionDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </label>

              <label className="ev-workstation-table__action-editor-field">
                <span>Add Note</span>
                <textarea
                  className="ev-workstation-table__input ev-workstation-table__textarea"
                  rows={4}
                  value={noteDraft}
                  placeholder="Add a note about what changed, what was done, or what needs follow-up."
                  disabled={disabled || isSaving}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </label>

              <div className="ev-workstation-table__action-editor-footer">
                <span className="ev-workstation-table__action-editor-meta">
                  {isSaving ? "Saving..." : "Cmd/Ctrl+Enter to save"}
                </span>
                <div className="ev-workstation-table__action-editor-actions">
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    disabled={saveDisabled}
                    onClick={() => {
                      void handleSave();
                    }}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    disabled={disabled || isSaving}
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <aside className="ev-workstation-table__expanded-notes">
              <div className="ev-workstation-table__expanded-notes-head">
                <span className="section-title">Recent Notes</span>
                <span className="ev-workstation-table__action-editor-meta">Latest 5</span>
              </div>
              {detailQuery.isLoading ? (
                <div className="ev-workstation-table__expanded-empty">Loading notes...</div>
              ) : detailQuery.error ? (
                <div className="ev-workstation-table__expanded-empty">
                  Could not load note history.
                </div>
              ) : recentEvents.length === 0 ? (
                <div className="ev-workstation-table__expanded-empty">
                  No timeline notes or recent workflow changes yet.
                </div>
              ) : (
                <div className="ev-workstation-table__expanded-history">
                  {recentEvents.map((event) => (
                    <div key={event.id} className="ev-workstation-table__history-item">
                      <div className="ev-workstation-table__history-head">
                        <span>{formatWorkflowTimelineEventLabel(event.eventType)}</span>
                        <span className="mono">{formatDateTime(event.createdAt)}</span>
                      </div>
                      <div className="ev-workstation-table__history-meta">
                        {event.actorEmail ?? event.source}
                      </div>
                      {event.note ? (
                        <SimpleMarkdown
                          markdown={event.note}
                          className="markdown-block markdown-block--workflow markdown-block--event"
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </div>
      </td>
    </tr>
  );
}

interface EvWorkflowTableProps {
  items: EvWorkstationProject[];
  savingProjectIds: ReadonlySet<string>;
  focusedProjectId: string | null;
  focusedWarmContactKey?: string | null;
  onOpenProjectDetail: (
    project: EvWorkstationProject["project"],
    options?: { workflowMode?: boolean }
  ) => void;
  onUpdateWorkflow: (projectId: string, patch: EvWorkflowPatch) => void;
  onUpdateIntroContact: (projectId: string, input: EvIntroContactUpdateInput) => void;
  onSaveActionPlan: (projectId: string, input: ActionPlanSaveInput) => Promise<void>;
}

const PAGE_SIZE = 50;

export function EvWorkflowTable({
  items,
  savingProjectIds,
  focusedProjectId,
  focusedWarmContactKey,
  onOpenProjectDetail,
  onUpdateWorkflow,
  onUpdateIntroContact,
  onSaveActionPlan,
}: EvWorkflowTableProps) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<EvWorkflowSortKey | null>(null);
  const [sortDir, setSortDir] = useState<EvWorkflowSortDir>("desc");
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [expandedIntroIds, setExpandedIntroIds] = useState<Set<string>>(new Set());

  // Auto-expand intro contacts when a warm contact is focused
  useEffect(() => {
    if (focusedProjectId && focusedWarmContactKey) {
      setExpandedIntroIds((prev) => {
        if (prev.has(focusedProjectId)) return prev;
        const next = new Set(prev);
        next.add(focusedProjectId);
        return next;
      });
    }
  }, [focusedProjectId, focusedWarmContactKey]);

  function handleSort(key: EvWorkflowSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "score" || key === "delta" ? "desc" : "asc");
    }
    setPage(0);
  }

  const sortedItems = useMemo(() => {
    const orderedItems = orderEvWorkflowItems(items, sortKey, sortDir);
    if (!focusedProjectId) return orderedItems;

    const focusIndex = orderedItems.findIndex((item) => item.project.id === focusedProjectId);
    if (focusIndex <= 0) return orderedItems;
    const focusedItem = orderedItems[focusIndex];
    if (!focusedItem) return orderedItems;

    return [
      focusedItem,
      ...orderedItems.slice(0, focusIndex),
      ...orderedItems.slice(focusIndex + 1),
    ];
  }, [focusedProjectId, items, sortKey, sortDir]);

  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE);

  // Clamp the current page if filters shrink the result set.
  useEffect(() => {
    if (page === 0 || page < totalPages) return;
    setPage(Math.max(totalPages - 1, 0));
  }, [page, totalPages]);

  useEffect(() => {
    if (!focusedProjectId) return;
    setPage(0);
  }, [focusedProjectId]);

  useEffect(() => {
    if (!expandedProjectId) return;
    if (sortedItems.some((item) => item.project.id === expandedProjectId)) return;
    setExpandedProjectId(null);
  }, [expandedProjectId, sortedItems]);

  const pagedItems = useMemo(
    () => sortedItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedItems, page]
  );

  function sortIndicator(key: EvWorkflowSortKey) {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  return (
    <section className="ev-workstation-table" data-ev-tour="workflow-table">
      <div className="ev-workstation-table__header">
        <span className="section-title">Active Queue</span>
        <span className="section-count">{items.length} rows</span>
      </div>

      <div className="ev-workstation-table__wrap">
        <table className="admin-users-table ev-workstation-table__grid">
          <thead>
            <tr>
              <th>Project</th>
              <th className="sortable-th" onClick={() => handleSort("score")}>
                Score{sortIndicator("score")}
              </th>
              <th
                className="sortable-th ev-workstation-table__col--trend"
                onClick={() => handleSort("delta")}
              >
                Trend{sortIndicator("delta")}
              </th>
              <th className="sortable-th" onClick={() => handleSort("stage")}>
                Stage{sortIndicator("stage")}
              </th>
              <th className="ev-workstation-table__col--intro-status">Intro Status</th>
              <th
                className="sortable-th ev-workstation-table__col--follow-up"
                onClick={() => handleSort("projectFollowUp")}
              >
                Project FU{sortIndicator("projectFollowUp")}
              </th>
              <th
                className="sortable-th ev-workstation-table__col--follow-up"
                onClick={() => handleSort("warmIntroFollowUp")}
              >
                Warm/Intro FU{sortIndicator("warmIntroFollowUp")}
              </th>
              <th
                className="sortable-th ev-workstation-table__col--next-steps"
                onClick={() => handleSort("nextAction")}
              >
                Next Steps{sortIndicator("nextAction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedItems.map((item) => {
              const delta = getDeltaValue(item.project);
              const deltaTone = getDeltaTone(delta);
              const scoreHistory = getScoreHistory(item.project);
              const allIntroContacts = item.introContacts ?? [];
              const introExpanded = expandedIntroIds.has(item.project.id);
              const visibleIntroContacts = allIntroContacts.slice(0, 2);
              const overflowIntroContacts = allIntroContacts.slice(2);
              const extraIntroCount = overflowIntroContacts.length;
              const isSavingRow = savingProjectIds.has(item.project.id);
              const isFocusedRow = focusedProjectId === item.project.id;
              const warmIntroFollowUpMeta =
                item.workflow.warmIntroFollowUpReason ??
                item.warmLead.summary ??
                "Warm intro follow-up lane is active.";
              const rowClassName = [
                item.flags.overdue ? "is-overdue" : "",
                item.flags.hasWarmLead ? "is-warm-lead" : "",
                isFocusedRow ? "is-focused" : "",
                isSavingRow ? "is-saving" : "",
              ]
                .filter(Boolean)
                .join(" ");
              const isExpanded = expandedProjectId === item.project.id;

              return (
                <Fragment key={item.project.id}>
                  {isExpanded ? (
                    <ExpandedActionPlanRow
                      item={item}
                      disabled={isSavingRow}
                      colSpan={8}
                      onClose={() => setExpandedProjectId(null)}
                      onSave={onSaveActionPlan}
                    />
                  ) : (
                    <tr
                      className={rowClassName}
                      aria-busy={isSavingRow}
                      data-ev-tour="workflow-table-row"
                    >
                      <td>
                        <div className="ev-workstation-table__identity">
                          <button
                            type="button"
                            className="ev-workstation-table__project-link"
                            onClick={() =>
                              onOpenProjectDetail(item.project, { workflowMode: true })
                            }
                          >
                            {item.project.name ?? item.project.twitter_handle}
                          </button>
                          <span className="handle">@{item.project.twitter_handle}</span>
                          {isSavingRow && (
                            <span className="mono ev-workstation-table__row-status">Saving...</span>
                          )}
                        </div>
                      </td>
                      <td className="mono">{formatScore(item.project.current_alpha_score)}</td>
                      <td className="ev-workstation-table__col--trend">
                        <div className="ev-workstation-table__trend">
                          <DeltaBadge project={item.project} delta={delta} tone={deltaTone} />
                          <InlineSparkline data={scoreHistory} />
                        </div>
                      </td>
                      <td data-ev-tour="workflow-table-stage">
                        <select
                          className="community-input ev-workstation-table__select ev-workstation-table__select--stage"
                          value={item.workflow.stage}
                          disabled={isSavingRow}
                          onChange={(e) =>
                            onUpdateWorkflow(item.project.id, {
                              stage: e.target.value as EvWorkflowPatch["stage"],
                            })
                          }
                        >
                          <option value="lead">lead</option>
                          <option value="group_created">group created</option>
                          <option value="call_completed">call completed</option>
                          <option value="passed_ic">passed ic</option>
                          <option value="qualified_for_sale">qualified for sale</option>
                          <option value="contract_shared">contract shared</option>
                          <option value="closed_onboarding">closed onboarding</option>
                          <option value="ngmi">ngmi</option>
                          <option value="no_token">no token</option>
                          <option value="no_for_now">no for now</option>
                        </select>
                      </td>
                      <td
                        className="ev-workstation-table__col--intro-status"
                        data-ev-tour="workflow-table-intro-status"
                      >
                        <div className="ev-workstation-table__intro-status">
                          {visibleIntroContacts.length > 0 ? (
                            <div className="ev-workstation-table__intro-list">
                              {visibleIntroContacts.map((contact) => {
                                const isWarmFocused =
                                  isFocusedRow &&
                                  focusedWarmContactKey != null &&
                                  `${contact.fundName}::${contact.contact.name}` ===
                                    focusedWarmContactKey;
                                return (
                                  <div
                                    key={contact.contactKey}
                                    className={`ev-workstation-table__intro-entry${isWarmFocused ? " ev-workstation-table__intro-entry--focused" : ""}`}
                                  >
                                    <span
                                      className="ev-workstation-table__intro-chip"
                                      title={formatIntroMatch(contact)}
                                    >
                                      {getIntroContactDisplayLabel(contact)}
                                    </span>
                                    <select
                                      className={`community-input ev-workstation-table__select ev-workstation-table__select--intro ${getIntroStatusClassName(contact.status)}`}
                                      value={contact.status}
                                      disabled={isSavingRow}
                                      onChange={(e) =>
                                        onUpdateIntroContact(item.project.id, {
                                          contactKey: contact.contactKey,
                                          fundName: contact.fundName,
                                          normalizedFundName: contact.normalizedFundName,
                                          status: e.target.value as EvIntroStatus,
                                          notes: contact.notes,
                                          contact: {
                                            ...contact.contact,
                                          },
                                        })
                                      }
                                    >
                                      <option value="not_asked">
                                        {getIntroStatusOptionLabel("not_asked")}
                                      </option>
                                      <option value="asked">
                                        {getIntroStatusOptionLabel("asked")}
                                      </option>
                                      <option value="intro_made">
                                        {getIntroStatusOptionLabel("intro_made")}
                                      </option>
                                      <option value="no_response">
                                        {getIntroStatusOptionLabel("no_response")}
                                      </option>
                                      <option value="not_interested">
                                        {getIntroStatusOptionLabel("not_interested")}
                                      </option>
                                      <option value="follow_up_needed">
                                        {getIntroStatusOptionLabel("follow_up_needed")}
                                      </option>
                                    </select>
                                  </div>
                                );
                              })}
                              {extraIntroCount > 0 && (
                                <div
                                  className={`ev-workstation-table__intro-overflow${introExpanded ? " is-expanded" : ""}`}
                                >
                                  <div className="ev-workstation-table__intro-overflow-inner">
                                    {overflowIntroContacts.map((contact) => {
                                      const isWarmFocused =
                                        isFocusedRow &&
                                        focusedWarmContactKey != null &&
                                        `${contact.fundName}::${contact.contact.name}` ===
                                          focusedWarmContactKey;
                                      return (
                                        <div
                                          key={contact.contactKey}
                                          className={`ev-workstation-table__intro-entry${isWarmFocused ? " ev-workstation-table__intro-entry--focused" : ""}`}
                                        >
                                          <span
                                            className="ev-workstation-table__intro-chip"
                                            title={formatIntroMatch(contact)}
                                          >
                                            {getIntroContactDisplayLabel(contact)}
                                          </span>
                                          <select
                                            className={`community-input ev-workstation-table__select ev-workstation-table__select--intro ${getIntroStatusClassName(contact.status)}`}
                                            value={contact.status}
                                            disabled={isSavingRow}
                                            onChange={(e) =>
                                              onUpdateIntroContact(item.project.id, {
                                                contactKey: contact.contactKey,
                                                fundName: contact.fundName,
                                                normalizedFundName: contact.normalizedFundName,
                                                status: e.target.value as EvIntroStatus,
                                                notes: contact.notes,
                                                contact: {
                                                  ...contact.contact,
                                                },
                                              })
                                            }
                                          >
                                            <option value="not_asked">
                                              {getIntroStatusOptionLabel("not_asked")}
                                            </option>
                                            <option value="asked">
                                              {getIntroStatusOptionLabel("asked")}
                                            </option>
                                            <option value="intro_made">
                                              {getIntroStatusOptionLabel("intro_made")}
                                            </option>
                                            <option value="no_response">
                                              {getIntroStatusOptionLabel("no_response")}
                                            </option>
                                            <option value="not_interested">
                                              {getIntroStatusOptionLabel("not_interested")}
                                            </option>
                                            <option value="follow_up_needed">
                                              {getIntroStatusOptionLabel("follow_up_needed")}
                                            </option>
                                          </select>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {extraIntroCount > 0 && (
                                <button
                                  type="button"
                                  className={`ev-workstation-table__intro-expand${introExpanded ? " is-expanded" : ""}`}
                                  onClick={() =>
                                    setExpandedIntroIds((prev) => {
                                      const next = new Set(prev);
                                      if (introExpanded) {
                                        next.delete(item.project.id);
                                      } else {
                                        next.add(item.project.id);
                                      }
                                      return next;
                                    })
                                  }
                                >
                                  {introExpanded ? "show less" : `+${extraIntroCount} more`}
                                  <span className="ev-workstation-table__intro-expand-chevron">
                                    ▾
                                  </span>
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="ev-workstation-table__intro-meta">
                              {item.warmLead.matchedFundNames.length > 0
                                ? item.warmLead.matchedFundNames.slice(0, 2).join(", ")
                                : "No matched contacts"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="ev-workstation-table__col--follow-up"
                        data-ev-tour="workflow-table-project-follow-up"
                      >
                        <div
                          className={`ev-workstation-table__date-wrap${item.flags.projectFollowUpOverdue ? " is-overdue" : item.flags.projectFollowUpDueToday ? " is-due-today" : ""}`}
                        >
                          <input
                            type="date"
                            className="ev-workstation-table__input"
                            value={getProjectFollowUpDate(item.workflow) ?? ""}
                            disabled={isSavingRow}
                            onClick={(e) => showNativeDatePicker(e.currentTarget)}
                            onChange={(e) =>
                              onUpdateWorkflow(item.project.id, {
                                projectFollowUpDate: e.target.value || null,
                              })
                            }
                          />
                          <span className="mono ev-workstation-table__date-meta">
                            {item.workflow.nextTouchpointType !== "unclear"
                              ? `${item.workflow.nextTouchpointType} via ${item.workflow.nextTouchpointChannel}`
                              : formatDate(item.workflow.lastContactAt)}
                          </span>
                        </div>
                      </td>
                      <td
                        className="ev-workstation-table__col--follow-up"
                        data-ev-tour="workflow-table-warm-intro-follow-up"
                      >
                        {item.warmLead.hasWarmLead || (item.introContacts?.length ?? 0) > 0 ? (
                          <div
                            className={`ev-workstation-table__date-wrap${item.flags.warmIntroFollowUpOverdue ? " is-overdue" : item.flags.warmIntroFollowUpDueToday ? " is-due-today" : ""}`}
                          >
                            <input
                              type="date"
                              className="ev-workstation-table__input"
                              value={item.workflow.warmIntroFollowUpDate ?? ""}
                              disabled={isSavingRow}
                              onClick={(e) => showNativeDatePicker(e.currentTarget)}
                              onChange={(e) =>
                                onUpdateWorkflow(item.project.id, {
                                  warmIntroFollowUpDate: e.target.value || null,
                                })
                              }
                            />
                            <span
                              className="mono ev-workstation-table__date-meta ev-workstation-table__date-meta--warm-intro"
                              title={warmIntroFollowUpMeta}
                            >
                              {warmIntroFollowUpMeta}
                            </span>
                          </div>
                        ) : (
                          <div className="ev-workstation-table__date-wrap ev-workstation-table__date-wrap--empty">
                            <span className="mono ev-workstation-table__date-meta">
                              No warm lead
                            </span>
                          </div>
                        )}
                      </td>
                      <td
                        className="ev-workstation-table__next-action-cell"
                        data-ev-tour="workflow-table-next-action"
                      >
                        <div className="ev-workstation-table__action-inline">
                          <span
                            className={`ev-workstation-table__action-text${item.workflow.nextAction ? "" : " is-empty"}`}
                          >
                            {getActionPlanPreviewText(item.workflow.nextAction)}
                          </span>
                          <button
                            type="button"
                            className="ev-workstation-table__action-toggle"
                            disabled={isSavingRow}
                            onClick={() => setExpandedProjectId(item.project.id)}
                          >
                            {getActionPlanButtonLabel()}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-bar">
          <button
            type="button"
            className="pagination-btn"
            disabled={page === 0}
            onClick={() => setPage(0)}
          >
            First
          </button>
          <button
            type="button"
            className="pagination-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <span className="pagination-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="pagination-btn"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
          <button
            type="button"
            className="pagination-btn"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(totalPages - 1)}
          >
            Last
          </button>
        </div>
      )}
    </section>
  );
}
